import { EventSource } from "../event-source";
import { View, ViewConstructor, Component } from "../view";
import Router from "../router/index";
import { LazyTemplate } from "../template";
import * as Dom from "../helpers/dom";
import ViewRendeder from "../view/renderer";
import ActionBar from "../action-bar/index";
import ModalManager from "../modal/manager";
import Api, { ApiCallResponse, OfflineError } from "./api";
import ToastManager from "../toast/index";
import ViewProcessorManager from "../view/processor";
import FormViewProcessor from "../view/processor/form";
import FlipcardViewProcessor from "../view/processor/flipcard";
import AppHeader from "./header";
import DataBindViewProcessor from "../view/processor/data-bind";
import { AppSettings } from '../config';
import { BaseDatabase } from "../db/index";
import Synchronizer, { SynchronizerEvent } from "../synchronizer/index";
import { User, UserAccount } from "../db/models";
import { CurrentUser, AttributeKey, Attribute } from "../db/models/attributes";
import ViewActions from "../view/actions";
import ErrorView from "../views/error/index";
import ErrorModal from "../modals/error/index";
import AuthenticationModal from "../modals/authentication/index";
import { InfoButton } from "../action-bar/items/index";
import { AppRequest } from "../router/request";
import * as String from '../helpers/string';
import { StringFormatArgs } from "../helpers/string";

export abstract class BaseApp extends EventSource
{
    public currentViewType: ViewConstructor;
    public currentView: View;
    public settings: AppSettings;
    public constance: Constance;
    public defaultRoute = '/';
    public router: Router;
    public actionBar: ActionBar;
    public modals: ModalManager;
    public toasts: ToastManager;
    public viewProcessors: ViewProcessorManager;
    public api: Api;
    public actions: ViewActions;

    protected mainElement: HTMLElement;
    protected loadingElement: HTMLElement;

    public isAuthenticated: boolean;
    public viewContainer: HTMLElement;

    protected i18n: ClientSynci18n;

    protected errorView = ErrorView;
    public header: AppHeader;
    public db: BaseDatabase;
    public synchronizer: Synchronizer;
    public user: User;
    public userSettings: CurrentUser;
    public request: AppRequest;

    protected syncQueueIndicatorElement: HTMLElement;
    private _currentLanguage: string;
    public get currentLanguage(): string { return this._currentLanguage;}

    public get apiToken(): string { 
        return this.userSettings.token;
    }

    public get SyncQueueIndicatorVisibility(): boolean
    {
        return this.syncQueueIndicatorElement.style.display == 'block';
    }

    public set SyncQueueIndicatorVisibility(value: boolean)
    {
        this.syncQueueIndicatorElement.style.display = value ? 'block' : 'none';
    }

    public barcodeAuthentication: BarcodeAuthentication = {
        isEnabled: false,
    }
    
    constructor()
    {
        super();
        this.barcodeAuthentication.user = JSON.parse(localStorage.getItem('barcode-authentication') || 'null');

        this.configure().then(() =>
        {
            return this.init();
        }).then(() =>
        {
            return this.run();
        });
    }

    public configure(): PromiseLike<void>
    {
        doT.templateSettings = {
            evaluate:    /\{\{([\s\S]+?)\}\}/g,
            // encode and interpolate have been swapped, so that {{= }} defaults to encoded output.
            // use {{! }} for non-encoded output.
            interpolate: /\{\{!([\s\S]+?)\}\}/g,
            encode:      /\{\{=([\s\S]+?)\}\}/g,
            use:         /\{\{#([\s\S]+?)\}\}/g,
            define:      /\{\{##\s*([\w\.$]+)\s*(\:|=)([\s\S]+?)#\}\}/g,
            conditional: /\{\{\?(\?)?\s*([\s\S]*?)\s*\}\}/g,
            iterate:     /\{\{~\s*(?:\}\}|([\s\S]+?)\s*\:\s*([\w$]+)\s*(?:\:\s*([\w$]+))?\s*\}\})/g,
            varname: 'it',
            strip: true,
            append: true,
            selfcontained: false
          };
        
        this.loadingElement = document.querySelector('aside.app-loading') as HTMLElement;
        this.mainElement = document.querySelector('main.app') as HTMLElement;
        this.syncQueueIndicatorElement = document.querySelector('[data-sync-queue-indicator]');

        let metaTag = document.querySelector('meta[name="application-settings"]');
        let data = metaTag.getAttribute('content');
        let decoded = atob(data);
        
        this.settings = JSON.parse(decoded);
        this.api = new Api(this, this.settings.trackingcloudUrl);
        this.actionBar = new ActionBar(this);
        this.modals = new ModalManager(this);
        this.toasts = new ToastManager(this);
        this.router = new Router(this);
        this.viewProcessors = new ViewProcessorManager(this);
        this.header = new AppHeader(this);
        this.actions = new ViewActions(this);
        this.synchronizer = new Synchronizer(this);

        this.synchronizer.on(SynchronizerEvent.SYNC_QUEUE_PUSHED, () => this.SyncQueueIndicatorVisibility = true);
        this.synchronizer.on(SynchronizerEvent.SYNC_QUEUE_FLUSHED, () => this.SyncQueueIndicatorVisibility = false);
        
        this.configureRouter(this.router);
        this.configureViewProcessors(this.viewProcessors);

        this.viewContainer = <HTMLElement>document.querySelector('[data-view-container]');
        
        this.on('refreshRequired', this.onRefreshRequired);
        
        return this.initDatabase().then(() =>
        {
            return this.updateSyncQueueIndicator();
        }).then(() =>
        {
            return this.loadAttributes();
        })
    }

    public init(): PromiseLike<void>
    {
        this.synchronizer.init();
        return Promise.resolve();
    }

    protected initDatabase(): PromiseLike<void>
    {
        let promises: PromiseLike<any>[] = [];
        if (this.db !== undefined)
        {
            promises.push(this.db.open());
        }
        
        return Promise.all(promises).then(() =>
        {
            // Flatten promise array
        });
    }

    protected updateSyncQueueIndicator(): PromiseLike<void>
    {
        return this.synchronizer.hasMessagesInQueue().then((hasQueue) =>
        {
            this.SyncQueueIndicatorVisibility = hasQueue;
        });
    }

    protected run()
    {
        let dontRaise = false;
        return this.loadUserInstance().then(() =>
        {          
            this._currentLanguage = this.userSettings != null ? this.userSettings.language : navigator.language;
            return this.loadi18n(this._currentLanguage);
        }).then(() =>
        {
            this.configureActionbar(this.actionBar);
            if (this.isAuthenticated) {
                dontRaise = true; // Do not raise errors that were caused by failed sync
                return this.synchronizer.update(true);
            }
        }).then(() => dontRaise = false, (error) =>
        {
            if (error instanceof OfflineError || dontRaise)
            {
                console.log('[ClientSync]: Offline, unable to sync.', error);
                return this.router.run();
            }
            else
            {
                console.log('client sync failed')
                console.error(error);
                if (this.isAuthenticated) {
                    return this.router.run();
                } else {
                    this.error(error, new Error('Client synchronization failed.'));
                }
            }
            dontRaise = false;
        }).then(() =>
        {
            this.isLoading = false;
            return this.router.run();
        });
    }

    protected loadAttributes(): PromiseLike<void>
    {
        return Promise.all([
            this.db.Attribute.get(AttributeKey.CURRENT_USER),
            this.db.Attribute.get(AttributeKey.CONSTANCE),
        ]).then((results: [CurrentUser, Attribute]) =>
        {
            let user = results[0];
            let constance = results[1];

            if (user)
            {
                this.userSettings = user;
            }
            else
            {
                this.userSettings = undefined;
            }

            this.constance = constance != null ? constance.value : {};
        });
    }

    protected loadUserInstance() : PromiseLike<void>
    {
        if (this.userSettings == null)
        {
            return Promise.resolve();
        }
        return this.validateUserToken().then(() =>
        {
            return this.db.User.get(this.userSettings.id) as PromiseLike<User>;
        }).then((user) =>
        {
            this.user = User.createFrom(user, this.userSettings);
            this.isAuthenticated = true;

            this.trigger(AppEvents.AUTHENTICATED);
        });
    }

    protected validateUserToken()
    {
        return this.api.public(this.settings.clientSync.apiEndpoints.validateToken, {token: this.userSettings.token}).then((result: {success: boolean}) =>
        {            
            if (!result.success)
            {
                indexedDB.deleteDatabase(this.settings.clientSync.databaseName);
                location.reload();
            }
        }, (error) =>
        {
            if (error instanceof OfflineError)
            {
                // We are offline
            }
            else
            {
                throw error;
            }
        });
    }

    public authenticate(): PromiseLike<void>
    {
        if (this.settings.emulateAuthentication)
        {
            return Promise.resolve();
        }
        
        if (!this.isAuthenticated)
        {
            return this.modals.show(new AuthenticationModal()).then(() =>
            {
                this.isAuthenticated = true;
                this.trigger(AppEvents.AUTHENTICATED);

                if (this.userSettings.language != this._currentLanguage)
                {
                    return this.loadi18n(this.userSettings.language);
                }
                else
                {
                    return <PromiseLike<void>>Promise.resolve();
                }
            }).then(() =>
            {
                return this.synchronizer.pullUpdates(false);
            })
            .then(() => null, console.error) // Supress errors
            .then(() => {
                // This cannot work since we reload the whole document
                // No idea how it worked before...
                this.toasts.show(this.pgettext('Message', 'Successfully signed in!'));
                location.reload();
            });
        }
    }

    private unloadCurrentView()
    {
        let unloadPromise: PromiseLike<void>;
        
        if (this.currentView !== undefined && !this.currentView.isUnloaded)
        {
            unloadPromise = this.currentView.unload();
        }
        else
        {
            unloadPromise = Promise.resolve();
        }

        return unloadPromise;
    }
    
    public view(viewType: ViewConstructor, data?: any): PromiseLike<View>
    {
        return new Promise<View>((resolve, reject) =>
        {
            this.unloadCurrentView().then(() =>
            {
                this.currentViewType = viewType;
                this.currentView = new viewType(data);
                this.renderView(this.currentView, this.viewContainer).then(() =>
                {
                    resolve(this.currentView);
                });
            });
        });
    }

    public renderView(view: Component, containerElement?: HTMLElement, clearContainer = true): PromiseLike<void>
    {
        // We have a primary view
        if (containerElement == this.viewContainer)
        {   
            this.header.reset();
        }

        return this.viewProcessors.beforeRender(view).then(() =>
        {
            return view.beforeRender();
        }).then(() =>
        {
            return ViewRendeder.renderToElement(view);
        }).then(() =>
        {
            if (containerElement !== undefined)
            {
                if (clearContainer)
                {
                    Dom.emptyElement(containerElement);
                }

                containerElement.appendChild(view.element);
            }

            return this.viewProcessors.afterRender(view);
        }).then(() =>
        {
            return view.afterRender();
        }).then(() =>
        {

        });
    }

    public refreshCurrentView(data?: any): PromiseLike<void>
    {
        data = data !== undefined ? data: this.currentView.data;
        return this.view(this.currentViewType, data).then(() =>
        {

        });
    }

    public renderPartialView(view: Component, containerElement?: HTMLElement, clearContainer = false): PromiseLike<void>
    {
        return this.renderView(view, containerElement, clearContainer);
    }

    public error(error: Error, displayError?: Error)
    {
        this.isLoading = false;
        console.error(error);        
        this.modals.show(new ErrorModal(displayError, error));
    }

    public warning(error: Error, actualError?: Error)
    {
        console.warn(actualError !== undefined ? actualError : error);
        this.view(this.errorView, error);
    }

    protected loadi18n(languageCode: string): PromiseLike<void>
    {
        return fetch(`/language.json?language_code=${languageCode}`)
        .then((response) => {
            return response.json();
        }).then(i18n => {
            this.i18n = i18n;
        }, (error) =>
        {
            if (error instanceof OfflineError)
            {
                return <PromiseLike<void>>Promise.resolve();
            }
            else
            {
                console.error(error);
            }
        });
    }

    public configureRouter(router: Router)
    {

    }

    public configureActionbar(actionBar: ActionBar): void
    {
        //actionBar.add(InfoButton);
    }

    public configureViewProcessors(processors: ViewProcessorManager)
    {
        processors.add(FormViewProcessor);
        processors.add(DataBindViewProcessor);
    }

    public logout(): void
    {
        this.db.Attribute.delete(AttributeKey.CURRENT_USER).then(() => {
            location.hash = '';
            this.refresh();
        });
    }

    public refresh()
    {
        window.location.reload();
    }

    public formatString(str: string, args?: StringFormatArgs, autoEscape = true)
    {
        return String.format(str, args, autoEscape);
    }

    public pgettext(context: string, message: string, args?: StringFormatArgs, autoEscape = true): string
    {
        let key = `${context}:${message}`;

        if (this.i18n != null && this.i18n.translations[key] !== undefined)
        {
            return this.formatString(this.i18n.translations[key], args, autoEscape);
        }
        else
        {
            console.warn(`Unknown translation: "${message}" (context: "${context}"), args: `, args);
            return this.formatString(message, args, autoEscape);
        }
    }

    public ugettext(message: string, args?: StringFormatArgs): string
    {
        return this.pgettext('', message, args);
    }

    public get isLoading(): boolean
    {
        return this.loadingElement.classList.contains('visible');
    }

    public set isLoading(value: boolean)
    {
        if (value)
        {
            document.body.classList.add('loading');
        }
        else
        {
            document.body.classList.remove('loading');
        }
    }

    protected onRefreshRequired = () =>
    {
        this.promptInstallUpdate();
    };

    public promptInstallUpdate()
    {
        let html = `
        ${this.pgettext('Message', 'Update ready to install')}<br />
        <small>${this.pgettext('Message', 'Click to begin')}</small>`;

        this.toasts.show(html, 0, 'im im-spinner8 spin', true, false).then(() =>
        {
            location.reload();
        });
    }    
    
    public promptDownloadUpdate()
    {
        let html = `
        ${this.pgettext('Message', 'Update available')}<br />
        <small>${this.pgettext('Message', 'Click to download')}</small>`;

        this.toasts.show(html, 0, 'im im-cloud-download', true, false).then(() =>
        {
            location.reload();
        });
    }
}

export interface ClientSynci18n extends ApiCallResponse
{
    language_code: string;
    translations: {[key: string]: string};
}

export interface Constance
{
    [key: string]: Object;
}

export enum AppEvents
{
    AUTHENTICATED = 'authenticated'
}

export interface BarcodeAuthentication {
    isEnabled: boolean;

    user?: {
        user_id: number;
        account_id: number;
        customer_id: number;
        username: string;
        first_name: string;
        last_name: string;
        person_number: string;
        nfc_token: string;
        location_id: number;
        display_name: string;
    }
}